import { FormEvent, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { dialogStore, useDialog } from "../os/dialog";

/** Epoch seconds → YYYY-MM-DD for <input type="date">. */
function toInputDate(sec: number): string {
  const d = new Date(sec * 1000);
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

/** YYYY-MM-DD → epoch seconds at LOCAL midnight (start-of-day). */
function fromInputDate(s: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const t = new Date(`${s}T00:00:00`).getTime();
  return Number.isFinite(t) ? Math.floor(t / 1000) : null;
}

export function DialogHost() {
  const dlg = useDialog();
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState("");
  const [fromStr, setFromStr] = useState("");
  const [toStr, setToStr] = useState("");

  useEffect(() => {
    if (!dlg) return;
    setValue(dlg.defaultValue ?? "");
    setFromStr(toInputDate(dlg.defaultFrom ?? Math.floor(Date.now() / 1000) - 90 * 86400));
    setToStr(toInputDate(dlg.defaultTo ?? Math.floor(Date.now() / 1000)));
    const t = window.setTimeout(() => {
      if (dlg.kind === "prompt") inputRef.current?.focus();
      else inputRef.current?.blur();
    }, 10);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        dialogStore.dismiss();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("keydown", onKey);
    };
  }, [dlg?.id]);

  if (!dlg) return null;

  // dateRange validation: both dates parse, and from strictly before to.
  const fromSec = fromInputDate(fromStr);
  const toSec = fromInputDate(toStr);
  // End-of-day for `to` so a same-day range still covers that whole day.
  const toEndSec = toSec !== null ? toSec + 86399 : null;
  const rangeValid =
    dlg.kind !== "dateRange" ||
    (fromSec !== null && toEndSec !== null && fromSec < toEndSec);

  const onOk = () => {
    if (!rangeValid) return;
    if (dlg.kind === "prompt") dialogStore.settle(value);
    else if (dlg.kind === "dateRange") dialogStore.settle({ from: fromSec!, to: toEndSec! });
    else if (dlg.kind === "confirm") dialogStore.settle(true);
    else dialogStore.settle(true);
  };
  const onCancel = () => dialogStore.dismiss();

  const submit = (e: FormEvent) => {
    e.preventDefault();
    onOk();
  };

  const node = (
    <div
      className="os-dialog-backdrop"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) dialogStore.dismiss();
      }}
    >
      <form
        className="os-dialog glass glass-strong"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="os-dlg-title"
        aria-describedby="os-dlg-msg"
        onSubmit={submit}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div id="os-dlg-title" className="os-dialog-title">
          {dlg.title}
        </div>
        {dlg.message ? (
          <div id="os-dlg-msg" className="os-dialog-message">
            {dlg.message}
          </div>
        ) : (
          <div id="os-dlg-msg" className="os-dialog-message sr-only">
            Dialog
          </div>
        )}
        {dlg.kind === "prompt" && (
          <input
            ref={inputRef}
            className="os-dialog-input"
            value={value}
            placeholder={dlg.placeholder}
            onChange={(e) => setValue(e.target.value)}
            autoComplete="off"
          />
        )}
        {dlg.kind === "dateRange" && (
          <div className="os-dialog-daterange">
            <label className="os-dialog-datefield">
              <span>From</span>
              <input
                type="date"
                className="os-dialog-input"
                value={fromStr}
                max={toStr || undefined}
                onChange={(e) => setFromStr(e.target.value)}
              />
            </label>
            <label className="os-dialog-datefield">
              <span>To</span>
              <input
                type="date"
                className="os-dialog-input"
                value={toStr}
                min={fromStr || undefined}
                onChange={(e) => setToStr(e.target.value)}
              />
            </label>
            {!rangeValid && (
              <div className="os-dialog-datehint">
                Pick two valid dates with “From” before “To”.
              </div>
            )}
          </div>
        )}
        <div className="os-dialog-actions">
          {dlg.kind !== "alert" && (
            <button type="button" className="os-dialog-btn" onClick={onCancel}>
              {dlg.cancelLabel || "Cancel"}
            </button>
          )}
          <button
            type="submit"
            className={`os-dialog-btn primary ${dlg.danger ? "danger" : ""}`}
            autoFocus={dlg.kind !== "prompt"}
            disabled={!rangeValid}
          >
            {dlg.okLabel || "OK"}
          </button>
        </div>
      </form>
    </div>
  );
  return createPortal(node, document.body);
}
